import * as React from "react";
import {
  loadTimeTablePeriods,
  normalizePeriod,
  formatDateRange,
  type TimeTablePeriod,
} from "./timetablePeriods";

export type NormalizedPeriod = {
  id: number | null;
  from: Date | null;
  to: Date | null;
  label: string | null;
};

function dedupeAndSort(periods: TimeTablePeriod[]): NormalizedPeriod[] {
  const seen = new Set<string>();
  const out: NormalizedPeriod[] = [];

  for (const p of periods) {
    const n = normalizePeriod(p);
    const key = `${n.id ?? "null"}|${n.from?.toISOString() ?? "null"}|${
      n.to?.toISOString() ?? "null"
    }|${n.label ?? "null"}`;

    if (seen.has(key)) continue;
    seen.add(key);
    out.push(n);
  }

  out.sort((a, b) => {
    const af = a.from ? a.from.getTime() : -Infinity;
    const bf = b.from ? b.from.getTime() : -Infinity;
    return bf - af;
  });

  return out;
}

export function useTimetablePeriods(_rev?: number) {
  const [periods, setPeriods] = React.useState<NormalizedPeriod[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<unknown>(null);

  React.useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      setError(null);

      try {
        const list = await loadTimeTablePeriods();
        const normalized = dedupeAndSort(list);

        if (!cancelled) {
          setPeriods(normalized);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e);
          setPeriods([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, []);

  return { periods, loading, error };
}

export function useActivePeriod(rev?: number) {
  const { periods, loading, error } = useTimetablePeriods(rev);

  const active = React.useMemo(() => {
    if (!periods.length) return null;

    const now = Date.now();

    const byNow =
      periods.find(
        (p) => p.from && p.to && p.from.getTime() <= now && now < p.to.getTime()
      ) ?? null;

    if (byNow) return byNow;

    return periods[0] ?? null;
  }, [periods]);

  const { fromStr, toStr } = React.useMemo(() => {
    return formatDateRange(active?.from ?? null, active?.to ?? null, "sv-SE");
  }, [active]);

  return {
    loading,
    error,
    label: active?.label ?? null,
    from: active?.from ?? null,
    to: active?.to ?? null,
    fromStr,
    toStr,
    activeId: active?.id ?? null,
  };
}

export function useSelectedPeriod(
  periods: NormalizedPeriod[],
  preferLabel?: string | null
) {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!periods.length || selectedId !== null) return;

    const now = Date.now();

    const byLabel = preferLabel
      ? periods.find((p) => p.label === preferLabel)
      : null;

    const byNow = periods.find(
      (p) => p.from && p.to && p.from.getTime() <= now && now < p.to.getTime()
    );

    const initial = byLabel ?? byNow ?? periods[0] ?? null;

    if (initial?.id != null) {
      setSelectedId(String(initial.id));
    }
  }, [periods, preferLabel, selectedId]);

  const selected = React.useMemo(
    () =>
      periods.find((p) => String(p.id ?? "") === (selectedId ?? "")) ?? null,
    [periods, selectedId]
  );

  return { selectedId, setSelectedId, selected };
}
